import * as React from 'react'
import { Check, Copy, Link2, Loader2, RefreshCw } from 'lucide-react'
import { useReplaceVenueShareLink, useVenueShareLink } from '@/hooks/usePublicSchedule'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import type { Instant } from '@lib/validators/temporal.validator'

/**
 * When the crew last opened it, in the words a person would use.
 *
 * Takes an `Instant`, not a `string`: the DTO brands it, so it has already been
 * through `z.string().datetime()` at the boundary and there is no unparseable
 * date left for this to defend against.
 */
function lastUsedLabel(iso: Instant | null): string {
  if (iso === null) return 'Not opened yet'
  const at = new Date(iso)
  const minutes = Math.round((Date.now() - at.getTime()) / 60_000)
  if (minutes < 2) return 'Opened just now'
  if (minutes < 60) return `Opened ${minutes} minutes ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Opened ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `Opened ${days} ${days === 1 ? 'day' : 'days'} ago`
  return `Opened ${at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
}

/**
 * The venue's one public link.
 *
 * The URL comes from the API relative (`/s/…`) and is made absolute here against
 * wherever this app is being served, so the same row produces a working link on
 * localhost, staging and production without the API having to be told which one
 * it is talking to.
 */
export function VenueShareLinkSection({
  venueId,
  canEdit,
}: {
  venueId: string
  /** Edit adds creating the link, and revoking and replacing it; without it the link is only shown and copied. */
  canEdit: boolean
}): React.ReactElement {
  const linkQuery = useVenueShareLink(venueId)
  const replace = useReplaceVenueShareLink(venueId)
  const [copied, setCopied] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [failure, setFailure] = React.useState<string | null>(null)
  // The section stays mounted when the next venue is already loaded. A question
  // asked about the last venue's link is not one to answer about this one's.
  const [shownFor, setShownFor] = React.useState(venueId)
  if (shownFor !== venueId) {
    setShownFor(venueId)
    setConfirming(false)
    setCopied(false)
    setFailure(null)
  }

  const link = linkQuery.data?.link ?? null
  const absolute = link === null ? null : new URL(link.url, window.location.origin).toString()

  async function copy(): Promise<void> {
    if (absolute === null) return
    try {
      await navigator.clipboard.writeText(absolute)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Refused (no permission, insecure context). The URL is on screen and
      // selectable, so there is still a way through.
      setCopied(false)
    }
  }

  function mint(): void {
    setFailure(null)
    replace.mutate(undefined, {
      onSuccess: () => setConfirming(false),
      onError: (cause) => setFailure(requestErrorMessage(cause)),
    })
  }

  if (linkQuery.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the share link…
      </p>
    )
  }

  if (linkQuery.isError) {
    return (
      <ErrorState message={requestErrorMessage(linkQuery.error)} onRetry={() => void linkQuery.refetch()} />
    )
  }

  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-sm text-ink-muted">
        Anyone with this link can see the next two weeks here: the dates, the spaces, each plan and
        its setup instructions. It never shows an event’s name, its clients or its guest count. It is
        the same link every time, so a setup crew can bookmark it once.
      </p>

      {link === null || absolute === null ? (
        <Card className="space-y-3 p-4">
          <p className="m-0 text-sm text-ink">This venue has no share link yet.</p>
          {canEdit ? (
            <Button onClick={mint} disabled={replace.isPending} className="gap-2">
              {replace.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Link2 className="h-4 w-4" aria-hidden />
              )}
              {replace.isPending ? 'Creating…' : 'Create share link'}
            </Button>
          ) : null}
          {failure === null ? null : <FormError>{failure}</FormError>}
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-line bg-surface-sunken px-3 py-2 font-mono text-sm">
              {absolute}
            </code>
            <Button variant="outline" onClick={() => void copy()} className="gap-2">
              {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted">{lastUsedLabel(link.lastUsedAt)}</p>
            {canEdit ? (
              <Button variant="ghost" size="sm" onClick={() => setConfirming(true)} className="gap-2">
                <RefreshCw className="h-4 w-4" aria-hidden /> Revoke and replace
              </Button>
            ) : null}
          </div>
          {failure === null ? null : <FormError>{failure}</FormError>}
        </div>
      )}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace this link?</DialogTitle>
            {/* The cost is the whole point of asking: every bookmark made from
                the old link stops working, and nothing tells the person holding
                it except the page, which says to ask the venue for the new one. */}
            <DialogDescription>
              The current link stops working immediately. Anyone who bookmarked it — your setup crew
              included — will need the new one. Do this if the link has gone somewhere it shouldn’t.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={replace.isPending}>
              Cancel
            </Button>
            <Button pending={replace.isPending} variant="destructive" onClick={mint} className="gap-2">
              {replace.isPending ? 'Replacing…' : 'Replace link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
